export type ThemeMode = string;
export type ResolvedTheme = string;

export const THEME_STORAGE_KEY = 'app-theme-preference';

export const getSystemTheme = (): ResolvedTheme =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export const getStoredTheme = (): ThemeMode =>
  (localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode) || 'system';

export const resolveTheme = (theme: ThemeMode): ResolvedTheme =>
  theme === 'system' ? getSystemTheme() : theme;

export const applyTheme = (resolved: ResolvedTheme) => {
  document.documentElement.setAttribute('data-bs-theme', resolved);
};

export const initTheme = (onResolve?: (resolved: ResolvedTheme) => void): (() => void) => {
  const apply = (resolved: ResolvedTheme) => {
    applyTheme(resolved);
    onResolve?.(resolved);
  };

  apply(resolveTheme(getStoredTheme()));

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = (e: MediaQueryListEvent) => {
    if (getStoredTheme() === 'system') {
      apply(e.matches ? 'dark' : 'light');
    }
  };

  mediaQuery.addEventListener('change', handleChange);
  return () => mediaQuery.removeEventListener('change', handleChange);
};

export const setThemePreference = (theme: ThemeMode): ResolvedTheme => {
  const normalized = theme.toLowerCase();
  if (localStorage.getItem(THEME_STORAGE_KEY) !== normalized) {
    localStorage.setItem(THEME_STORAGE_KEY, normalized);
  }
  const resolved = resolveTheme(normalized);
  if (document.documentElement.getAttribute('data-bs-theme') !== resolved) {
    applyTheme(resolved);
  }
  return resolved;
};
